import type { CSSProperties, FC } from 'react';
import { DatePicker } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { Calendar } from 'lucide-react';

export type IpcDateRangeValue = [string | undefined, string | undefined];

export type IpcDateRangePickerVariant = 'form' | 'filter';

export type IpcDateRangePickerProps = {
  value?: [string | null | undefined, string | null | undefined];
  onChange?: (range: IpcDateRangeValue) => void;
  variant?: IpcDateRangePickerVariant;
  placeholder?: [string, string];
  allowClear?: boolean;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  /** 精确到时分秒；值为 `YYYY-MM-DD HH:mm:ss` */
  showTime?: boolean;
};

function toDayjs(value?: string | null): Dayjs | null {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d : null;
}

function formatValue(d: Dayjs | null | undefined, withTime: boolean): string | undefined {
  if (!d || !d.isValid()) return undefined;
  return d.format(withTime ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD');
}

/**
 * IPC 日期范围
 * - form: 单框 RangePicker + 左图标
 * - filter: 起止两个 DatePicker + ~
 */
export const IpcDateRangePicker: FC<IpcDateRangePickerProps> = ({
  value,
  onChange,
  variant = 'form',
  placeholder = ['开始日期', '结束日期'],
  allowClear = true,
  disabled = false,
  className,
  style,
  showTime = false,
}) => {
  const start = value?.[0] ?? undefined;
  const end = value?.[1] ?? undefined;
  const displayFormat = showTime ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD';
  const timeProps = showTime
    ? { showTime: { format: 'HH:mm:ss' } as const }
    : {};

  if (variant === 'filter') {
    return (
      <span className={['ipc-date-range', 'ipc-date-range--filter', className].filter(Boolean).join(' ')} style={style}>
        <span className="ipc-date-shell ipc-date-shell--filter">
          <Calendar size={14} className="ipc-date-shell__icon" aria-hidden />
          <DatePicker
            value={toDayjs(start)}
            onChange={d => onChange?.([formatValue(d, showTime), end])}
            format={displayFormat}
            placeholder={placeholder[0]}
            allowClear={allowClear}
            disabled={disabled}
            size="middle"
            getPopupContainer={() => document.body}
            className="ipc-date ipc-date--form ipc-date--with-icon"
            {...timeProps}
          />
        </span>
        <span className="ipc-date-range__sep" aria-hidden>~</span>
        <span className="ipc-date-shell ipc-date-shell--filter">
          <Calendar size={14} className="ipc-date-shell__icon" aria-hidden />
          <DatePicker
            value={toDayjs(end)}
            onChange={d => onChange?.([start, formatValue(d, showTime)])}
            format={displayFormat}
            placeholder={placeholder[1]}
            allowClear={allowClear}
            disabled={disabled}
            size="middle"
            getPopupContainer={() => document.body}
            className="ipc-date ipc-date--form ipc-date--with-icon"
            {...timeProps}
          />
        </span>
      </span>
    );
  }

  return (
    <span className={['ipc-date-shell', 'ipc-date-shell--form', className].filter(Boolean).join(' ')} style={style}>
      <Calendar size={14} className="ipc-date-shell__icon" aria-hidden />
      <DatePicker.RangePicker
        value={[toDayjs(start), toDayjs(end)]}
        onChange={dates => {
          onChange?.([formatValue(dates?.[0], showTime), formatValue(dates?.[1], showTime)]);
        }}
        format={displayFormat}
        placeholder={placeholder}
        allowClear={allowClear}
        disabled={disabled}
        size="middle"
        getPopupContainer={() => document.body}
        className="ipc-date ipc-date--form ipc-date--range ipc-date--with-icon"
        {...timeProps}
      />
    </span>
  );
};
